'use client';

import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * Breakpoint indicator that shows the current responsive breakpoint
 * Only visible in development mode
 */
export default function BreakpointIndicator() {
  const { t } = useTranslate();
  // Use client-side detection instead of environment variables
  const [isProduction, setIsProduction] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLocalStorageAvailable, setIsLocalStorageAvailable] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    // Check for production indicators only on the client side
    setIsMounted(true);
    const hostname = window.location.hostname;
    setIsProduction(hostname.includes('vercel.app') || hostname === 'bettr.golf' || hostname === 'www.bettr.golf');

    // Track window width
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    // Check if localStorage is available
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      setIsLocalStorageAvailable(true);

      // Load expanded state from localStorage
      const savedState = localStorage.getItem('breakpoint_expanded');
      if (savedState !== null) {
        setIsExpanded(savedState === 'true');
      }
    } catch (e) {
      setIsLocalStorageAvailable(false);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save expanded state to localStorage
  useEffect(() => {
    if (isLocalStorageAvailable) {
      localStorage.setItem('breakpoint_expanded', String(isExpanded));
    }
  }, [isExpanded, isLocalStorageAvailable]);

  // Don't render anything during SSR or in production
  if (!isMounted || isProduction) return null;

  return (
    <div>
      {isExpanded ? (
        <div className="h-44 w-72 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg bg-gray-800/90 p-3 shadow-lg backdrop-blur-sm">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">{t('component.breakpoint.title', 'Breakpoints')}</h3>
            <button
              className="rounded-full bg-gray-700 p-1 text-gray-300 hover:bg-gray-600 hover:text-white"
              onClick={() => setIsExpanded(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="size-3" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-2 text-xs">
            <div className="grid grid-cols-6 gap-2">
              <div
                className={cn(
                  'flex h-6 w-8 items-center justify-center rounded font-medium',
                  'block sm:hidden md:hidden lg:hidden xl:hidden 2xl:hidden',
                  'bg-amber-600/90 text-white',
                )}>
                {t('component.breakpoint.xs', 'XS')}
              </div>
              <div
                className={cn(
                  'flex h-6 w-8 items-center justify-center rounded font-medium',
                  'hidden sm:flex md:hidden lg:hidden xl:hidden 2xl:hidden',
                  'bg-green-600/90 text-white',
                )}>
                {t('component.breakpoint.sm', 'SM')}
              </div>
              <div
                className={cn(
                  'flex h-6 w-8 items-center justify-center rounded font-medium',
                  'hidden sm:hidden md:flex lg:hidden xl:hidden 2xl:hidden',
                  'bg-blue-600/90 text-white',
                )}>
                {t('component.breakpoint.md', 'MD')}
              </div>
              <div
                className={cn(
                  'flex h-6 w-8 items-center justify-center rounded font-medium',
                  'hidden sm:hidden md:hidden lg:flex xl:hidden 2xl:hidden',
                  'bg-purple-600/90 text-white',
                )}>
                {t('component.breakpoint.lg', 'LG')}
              </div>
              <div
                className={cn(
                  'flex h-6 w-8 items-center justify-center rounded font-medium',
                  'hidden sm:hidden md:hidden lg:hidden xl:flex 2xl:hidden',
                  'bg-pink-600/90 text-white',
                )}>
                {t('component.breakpoint.xl', 'XL')}
              </div>
              <div
                className={cn(
                  'flex h-6 w-8 items-center justify-center rounded font-medium',
                  'hidden sm:hidden md:hidden lg:hidden xl:hidden 2xl:flex',
                  'bg-red-600/90 text-white',
                )}>
                {t('component.breakpoint.2xl', '2XL')}
              </div>
            </div>
            <div className="mt-1 text-center text-gray-400">
              <span className="block sm:hidden md:hidden lg:hidden xl:hidden 2xl:hidden">
                {t('component.breakpoint.size-xs', 'Default: <640px')}
              </span>
              <span className="hidden sm:block md:hidden lg:hidden xl:hidden 2xl:hidden">
                {t('component.breakpoint.size-sm', 'SM: ≥640px')}
              </span>
              <span className="hidden sm:hidden md:block lg:hidden xl:hidden 2xl:hidden">
                {t('component.breakpoint.size-md', 'MD: ≥768px')}
              </span>
              <span className="hidden sm:hidden md:hidden lg:block xl:hidden 2xl:hidden">
                {t('component.breakpoint.size-lg', 'LG: ≥1024px')}
              </span>
              <span className="hidden sm:hidden md:hidden lg:hidden xl:block 2xl:hidden">
                {t('component.breakpoint.size-xl', 'XL: ≥1280px')}
              </span>
              <span className="hidden sm:hidden md:hidden lg:hidden xl:hidden 2xl:block">
                {t('component.breakpoint.size-2xl', '2XL: ≥1536px')}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex h-8 items-center gap-1.5 rounded-full px-3 shadow-lg hover:bg-gray-700"
          title={t('component.breakpoint.show-button-title', 'Show Breakpoints')}>
          <span
            className={cn('text-xs font-bold text-white', 'block sm:hidden md:hidden lg:hidden xl:hidden 2xl:hidden')}>
            <span className="rounded bg-amber-600/90 px-1.5 py-0.5">{t('component.breakpoint.xs', 'XS')}</span>
          </span>
          <span
            className={cn('text-xs font-bold text-white', 'hidden sm:block md:hidden lg:hidden xl:hidden 2xl:hidden')}>
            <span className="rounded bg-green-600/90 px-1.5 py-0.5">{t('component.breakpoint.sm', 'SM')}</span>
          </span>
          <span
            className={cn('text-xs font-bold text-white', 'hidden sm:hidden md:block lg:hidden xl:hidden 2xl:hidden')}>
            <span className="rounded bg-blue-600/90 px-1.5 py-0.5">{t('component.breakpoint.md', 'MD')}</span>
          </span>
          <span
            className={cn('text-xs font-bold text-white', 'hidden sm:hidden md:hidden lg:block xl:hidden 2xl:hidden')}>
            <span className="rounded bg-purple-600/90 px-1.5 py-0.5">{t('component.breakpoint.lg', 'LG')}</span>
          </span>
          <span
            className={cn('text-xs font-bold text-white', 'hidden sm:hidden md:hidden lg:hidden xl:block 2xl:hidden')}>
            <span className="rounded bg-pink-600/90 px-1.5 py-0.5">{t('component.breakpoint.xl', 'XL')}</span>
          </span>
          <span
            className={cn('text-xs font-bold text-white', 'hidden sm:hidden md:hidden lg:hidden xl:hidden 2xl:block')}>
            <span className="rounded bg-red-600/90 px-1.5 py-0.5">{t('component.breakpoint.2xl', '2XL')}</span>
          </span>
          {windowWidth > 0 && <span className="text-[10px] text-gray-400">{windowWidth}px</span>}
        </button>
      )}
    </div>
  );
}
